import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { useContent } from "../lib/content";

const bubbles = [
  { label: "House Policies", href: "#policies", bg: "#245e55", text: "#eae4da", testId: "bubble-policies", external: false },
  { label: "FAQ", href: "#faq", bg: "#808bc5", text: "#1d1d1b", testId: "bubble-faq", external: false },
  { label: "More About MAS", href: "https://masarts.link", bg: "#eac119", text: "#1d1d1b", testId: "bubble-about-mas", external: true },
];

export const LinkBubbles = () => (
  <div className="flex flex-wrap gap-4">
    {bubbles.map((b, i) => (
      <motion.a
        key={b.label}
        data-testid={b.testId}
        href={b.href}
        {...(b.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        initial={{ opacity: 0, y: 24, scale: 0.95 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="inline-flex items-center gap-2 rounded-full px-8 py-5 font-display font-extrabold text-lg sm:text-xl tracking-tight transition-transform duration-300 hover:scale-105 hover:-rotate-1"
        style={{ backgroundColor: b.bg, color: b.text }}
      >
        {b.label}
        <ArrowUpRight size={20} />
      </motion.a>
    ))}
  </div>
);

export const Faq = () => {
  const { t } = useContent();
  const [openIdx, setOpenIdx] = useState(0);

  const faqs = [1, 2, 3, 4].map((n) => ({
    q: t(`faq_${n}_q`),
    a: t(`faq_${n}_a`),
  }));

  return (
    <section id="faq" className="py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#1d1d1b]/50 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-[#eac119]" />
          {t("faq_kicker")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-extrabold tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.1]"
        >
          {t("faq_heading")}
        </motion.h2>

        <div className="mt-10 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <LinkBubbles />

          <div data-testid="faq-list" className="grid gap-3">
            {faqs.map((f, i) => {
              const open = openIdx === i;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: i * 0.06 }}
                  className="card-soft overflow-hidden"
                >
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-display font-extrabold tracking-tight">{f.q}</span>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${open ? "bg-[#eac119] rotate-45" : "bg-[#1d1d1b]/[0.06]"}`}>
                      <Plus size={15} />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p data-testid={`faq-answer-${i}`} className="px-6 pb-6 text-sm text-neutral-500 leading-relaxed font-medium">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
